/**
 * Choix cookies (bandeau v2, decision du proprietaire du 16/10).
 *
 * Stockage local `kadelio_cookie_consent` = { v: 3, analytics, date } :
 * - une seule finalite facultative, la mesure d'audience ;
 * - choix valable 6 mois, acceptation comme refus, puis nouvelle demande ;
 * - nouvelle demande aussi a chaque changement de version des finalites ;
 * - v3 (20/10, avis du juridique) : la page /cookies v2 oubliait des traceurs ;
 *   les choix v1 et v2, donnes sur une information incomplete, ne sont pas
 *   repris : tout le monde revoit le bandeau une fois.
 *
 * Module sans React : lu par le contexte, par trackEvent et par les tests.
 */

export const CONSENT_KEY = 'kadelio_cookie_consent';
export const CONSENT_VERSION = 3;
/** Duree de validite du choix, en mois calendaires. */
export const CONSENT_MONTHS = 6;

export interface StoredConsent {
  v: typeof CONSENT_VERSION;
  analytics: boolean;
  /** Date du choix, ISO 8601. */
  date: string;
}

/** Date + n mois calendaires (le 31 aout + 6 mois tombe fin fevrier). */
export function addMonths(from: Date, months: number): Date {
  const d = new Date(from.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d;
}

/** Date a laquelle le choix sera redemande. */
export function consentExpiresAt(consent: Pick<StoredConsent, 'date'>): Date {
  return addMonths(new Date(consent.date), CONSENT_MONTHS);
}

function isFresh(date: Date, now: Date): boolean {
  if (Number.isNaN(date.getTime())) return false;
  // Une date dans le futur (horloge deplacee) n'allonge pas la validite.
  if (date.getTime() > now.getTime() + 24 * 60 * 60 * 1000) return false;
  return now.getTime() < addMonths(date, CONSENT_MONTHS).getTime();
}

function safeRemove() {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* stockage indisponible */ }
}

/**
 * Choix en vigueur sur cet appareil, ou null s'il faut (re)demander.
 * Un choix expire, d'une autre version ou illisible est efface.
 */
export function readConsent(now: Date = new Date()): StoredConsent | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    safeRemove();
    return null;
  }
  if (!parsed || typeof parsed !== 'object') {
    safeRemove();
    return null;
  }
  const p = parsed as Record<string, unknown>;

  if (p.v === CONSENT_VERSION) {
    if (typeof p.analytics === 'boolean' && typeof p.date === 'string' && isFresh(new Date(p.date), now)) {
      return { v: CONSENT_VERSION, analytics: p.analytics, date: p.date };
    }
    safeRemove();
    return null;
  }

  // Ancienne version (v1, v2) ou inconnue : on redemande, et un ancien accord
  // ne laisse pas de cookies Google en attendant le nouveau choix.
  safeRemove();
  deleteGaCookies();
  return null;
}

/** Enregistre un choix (date du jour) et le renvoie. */
export function writeConsent(analytics: boolean, now: Date = new Date()): StoredConsent {
  const consent: StoredConsent = { v: CONSENT_VERSION, analytics, date: now.toISOString() };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(consent)); } catch { /* stockage indisponible */ }
  return consent;
}

/** Accord « mesure d'audience » en vigueur (absent, refuse ou expire : false). */
export function hasAnalyticsConsent(now: Date = new Date()): boolean {
  return readConsent(now)?.analytics === true;
}

/**
 * Supprime les cookies Google Analytics (_ga, _ga_<id>) de ce navigateur.
 * gtag les pose sur le domaine le plus haut possible (.kadelio.com) : on
 * efface sur l'hote et sur chacun de ses domaines parents.
 */
export function deleteGaCookies(): void {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n === '_ga' || n.startsWith('_ga_') || n === '_gid' || n === '_gat' || n.startsWith('_gat_'));
  if (!names.length) return;
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains: (string | null)[] = [null];
  for (let i = 0; i < parts.length - 1; i++) {
    const d = parts.slice(i).join('.');
    domains.push(d, `.${d}`);
  }
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}

/** Date d'un choix en toutes lettres : « 15 octobre 2026 ». */
export function formatConsentDate(d: Date): string {
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Etat de la mesure d'audience, pour les pastilles (Paramètres, page /cookies). */
export function audienceStatus(consent: { analytics: boolean } | null): { label: string; accepted: boolean } {
  if (!consent) return { label: 'Non choisie', accepted: false };
  return consent.analytics ? { label: 'Acceptée', accepted: true } : { label: 'Refusée', accepted: false };
}
